import { useCallback, useEffect } from 'react';
import { Form, message } from 'antd';
import type { Dayjs } from 'dayjs';
import dayjs from 'dayjs';
import { useClosingPackageCommands } from './useClosingPackageCommands';
import { useClosingPackageTaskModalData } from './useClosingPackageTaskModalData';
import {
  buildPrepareOriginalsBody,
  validatePrepareOriginalsSubmit,
} from '../lib/prepareOriginalsModalValidation';
import { mapClosingPackageApiError } from '../lib/errorMapping';

export type PrepareOriginalsModalFormValues = {
  preparedAt: Dayjs;
  comment: string;
};

export type UsePrepareOriginalsModalOptions = {
  applicationId: number;
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void | Promise<void>;
};

export function usePrepareOriginalsModal({
  applicationId,
  open,
  onClose,
  onSuccess,
}: UsePrepareOriginalsModalOptions) {
  const [form] = Form.useForm<PrepareOriginalsModalFormValues>();

  const { projection, contextView, contextLoading } = useClosingPackageTaskModalData({
    applicationId,
    open,
  });

  const { registerOriginalsPrepared, states } = useClosingPackageCommands();

  useEffect(() => {
    if (!open) {
      form.resetFields();
      return;
    }

    form.setFieldsValue({
      preparedAt: dayjs(),
      comment: '',
    });
  }, [form, open, applicationId]);

  const handleSubmit = useCallback(async () => {
    if (!projection) {
      message.error('Не удалось загрузить данные комплекта');
      return;
    }

    let values: PrepareOriginalsModalFormValues;
    try {
      values = await form.validateFields();
    } catch {
      return;
    }

    const validationIssue = validatePrepareOriginalsSubmit({
      eventAt: values.preparedAt,
      comment: values.comment,
    });

    if (validationIssue) {
      message.error(validationIssue.message);
      return;
    }

    try {
      await registerOriginalsPrepared({
        applicationId,
        body: buildPrepareOriginalsBody({
          comment: values.comment,
          preparedAt: values.preparedAt.toISOString(),
        }),
      }).unwrap();

      message.success('Подготовка оригиналов зарегистрирована');
      await onSuccess?.();
      onClose();
    } catch (error) {
      message.error(mapClosingPackageApiError(error));
    }
  }, [applicationId, form, onClose, onSuccess, projection, registerOriginalsPrepared]);

  return {
    form,
    projection,
    contextView,
    contextLoading,
    submitting: states.registerOriginalsPrepared.isLoading,
    handleSubmit,
  };
}
